import React from "react";
import "./ai-band.css";
import pythonMascot from "../../assets/images/ai-band/python-mascot.webp";
import blockMascot from "../../assets/images/ai-band/block-mascot.webp";
import { PYTHON_PACKAGES, BLOCK_EXTENSIONS } from "./ai-band-data";


const VARIANTS = {
  python: {
    mascot: pythonMascot,
    mascotAlt: "Cybo with Python's AI libraries",
    eyebrow: "AI-Ready Runtime",
    version: "v3.12",
    title: "Python, pre-loaded for real AI work",
    text: "Every project boots with the full machine-learning, computer-vision and data-science stack compiled and ready — students train models in their first lesson, not their fifth.",
    statLabel: "advanced libraries",
    items: PYTHON_PACKAGES,
    mobileScroll: true, 
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="2.6" /><circle cx="5" cy="6" r="1.9" /><circle cx="19" cy="6" r="1.9" /><circle cx="5" cy="18" r="1.9" /><circle cx="19" cy="18" r="1.9" /><path d="M6.6 7.1 9.9 10.4M17.4 7.1 14.1 10.4M6.6 16.9 9.9 13.6M17.4 16.9 14.1 13.6" /></svg>
    ),
  },
  block: {
    mascot: blockMascot,
    mascotAlt: "Cybo mascot",
    eyebrow: "AI in Block Coding",
    title: "Powerful extensions, ready to drag & drop",
    text: "Scratch-style blocks that plug straight into AI, robotics and sensor hardware — no code required.",
    statLabel: "extensions",
    items: BLOCK_EXTENSIONS,
    scrollable: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.6" /><rect x="14" y="3" width="7" height="7" rx="1.6" /><rect x="3" y="14" width="7" height="7" rx="1.6" /><path d="M14 17.5h7M17.5 14v7" /></svg>
    ),
  },
};

const SPEED = 0.45; 
const RESUME_AFTER_MS = 2500; 
const MOBILE_QUERY = "(max-width: 820px)";

function useAutoScroll(ref) {
  const hold = React.useRef(() => {});

  React.useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { return undefined; }

    const mobile = window.matchMedia(MOBILE_QUERY);
    let frame;
    let timer;
    let direction = 1;
    let paused = false;
    let position = el.scrollLeft;

    const pause = () => { paused = true; clearTimeout(timer); };
    const resumeLater = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { position = el.scrollLeft; paused = false; }, RESUME_AFTER_MS);
    };
    const hold_ = () => { pause(); resumeLater(); };

    const tick = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (!paused && mobile.matches && max > 1) {
        position += SPEED * direction;
        if (position >= max) { position = max; direction = -1; }
        else if (position <= 0) { position = 0; direction = 1; }
        el.scrollLeft = position;
      }
      frame = requestAnimationFrame(tick);
    };

    const startEvents = ["pointerenter", "pointerdown", "touchstart", "focusin", "wheel"];
    const endEvents = ["pointerleave", "pointerup", "touchend", "focusout"];
    startEvents.forEach((name) => el.addEventListener(name, pause, { passive: true }));
    endEvents.forEach((name) => el.addEventListener(name, resumeLater, { passive: true }));
    el.addEventListener("wheel", hold_, { passive: true });

    hold.current = hold_;
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      startEvents.forEach((name) => el.removeEventListener(name, pause));
      endEvents.forEach((name) => el.removeEventListener(name, resumeLater));
      el.removeEventListener("wheel", hold_);
    };
  }, [ref]);

  return () => hold.current();
}

const Arrow = ({ points }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points={points} /></svg>
);

const AiBand = ({ variant }) => {
  const config = VARIANTS[variant];
  const scrollRef = React.useRef(null);
  const holdAutoScroll = useAutoScroll(scrollRef); // hooks first, before the early return below

  const storageKey = "csLab.aiBand." + variant;
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch (_) { return false; }
  });

  if (!config) { return null; }

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch (_) { /* storage unavailable */ }
  };

  const scrollBy = (left) => {
    holdAutoScroll();
    scrollRef.current?.scrollBy({ left, behavior: "smooth" });
  };
  const hasArrows = config.scrollable || config.mobileScroll;

  return (
    <div className={"ai-band" + (collapsed ? " collapsed" : "")}>
      <div className="ai-band-body">
        <div className="ai-glow-a" /><div className="ai-glow-b" />
        <div className="ai-sheen" />
        <div className="ai-band-inner ai-band-split">
          <div className="ai-hero"><img src={config.mascot} alt={config.mascotAlt} /></div>
          <div className="ai-band-main">
            <div className="ai-band-head">
              <span className="ai-band-ico">{config.icon}</span>
              <div className="ai-band-copy">
                <div className="ai-eyebrow">
                  <span className="ai-live" />{config.eyebrow}
                  {config.version && <span className="ai-ver">{config.version}</span>}
                </div>
                <h3 className="ai-band-t">{config.title}</h3>
                <p className="ai-band-s">{config.text}</p>
              </div>
              <div className="ai-stat">
                <b>{config.items.length}</b><i>{config.statLabel}</i>
                {hasArrows && (
                  <div className={"ai-stat-arrows" + (config.scrollable ? "" : " ai-arrows-mobile-only")}>
                    <button type="button" className="ai-pkgs-arrow" onClick={() => scrollBy(-280)} aria-label="Show previous"><Arrow points="15 6 9 12 15 18" /></button>
                    <button type="button" className="ai-pkgs-arrow" onClick={() => scrollBy(280)} aria-label="Show more"><Arrow points="9 6 15 12 9 18" /></button>
                  </div>
                )}
              </div>
              <button type="button" className="ai-collapse" onClick={toggleCollapsed} aria-expanded={!collapsed}
                aria-label={collapsed ? "Expand" : "Collapse"} title={collapsed ? "Expand" : "Collapse"}>
                <span />
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
              </button>
            </div>
            <div className={config.scrollable ? "ai-pkgs-wrap" : undefined}>
              <div className={"ai-pkgs" + (config.scrollable ? " ai-pkgs-scroll" : "") + (config.mobileScroll ? " ai-pkgs-m-scroll" : "")} ref={scrollRef}>
                {config.items.map((item, index) => (
                  <div className="ai-pkg" key={item.name + index}>
                    <span className="ai-logo"><img src={item.logo} alt="" /></span>
                    <span className="ai-pkg-txt"><b>{item.name}</b><i>{item.description}</i></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiBand;
